import { useCallback } from 'react'
import { useLocalisedValidations } from '@user/utils/useLocalisedValidations'
import {
  detectAndConvertDateTime,
  localizationDateFormat,
} from '../../functions'
import { format } from 'date-fns'
import { ActionMode, ImportEntityType } from '../../typings'
import { serviceFields } from '../../constants'
import { useSelector } from 'react-redux'
import { selectAllServiceFrequencies } from '@user/store/serviceFrequency/slice'
import { selectAllServiceProductsForTenant } from '@user/store/serviceProducts'
import { ImportUtilityModel } from '../../AddImportUtilityDrawer/typings'
import {
  isValidPhone,
  updatePhoneValueSearch,
} from 'local/common/src/utils/validation'
import {
  currencyString as formatCurrency,
  formatPercentage,
} from '@sales-center/common'
import { selectAllUsers } from '@user/store/users'
import { useSelectCustomFieldData } from '@user/store/customFields'

export function useValidate(importSettings: ImportUtilityModel | null) {
  const allServiceFrequencies = useSelector(selectAllServiceFrequencies)
  const allServices = useSelector(selectAllServiceProductsForTenant)
  const allUsers = useSelector(selectAllUsers)

  const { validatePostalCode } = useLocalisedValidations()

  const keysServiceFields = serviceFields

  const customFieldData = useSelectCustomFieldData()

  const validateUSCAPostalCode = useCallback(
    (record) => {
      const newRecord = { ...record }
      const { postalCode } = newRecord.row

      if (postalCode?.value) {
        const errorMessage = validatePostalCode(postalCode.value)

        if (errorMessage) {
          postalCode.info = [
            {
              message: errorMessage,
              level: 'error',
            },
          ]
        } else {
          postalCode.info = []
        }
      } else {
        postalCode.info = []
      }

      return newRecord
    },
    [validatePostalCode],
  )

  const validatePhoneNumber = useCallback(
    (phoneColumn: string, extensionColumn: string, row: any) => {
      if (!row) return

      row[phoneColumn].info = []

      const phoneNumber = row[phoneColumn].value
      const phoneExtension = row[extensionColumn].value

      if (!phoneNumber && !phoneExtension) {
        return
      }

      if (!phoneNumber && phoneExtension) {
        row[phoneColumn].info.push({
          message: 'Phone Number is required when an extension is provided',
          level: 'error',
        })

        return
      }

      const updatedPhoneNumber = updatePhoneValueSearch(phoneNumber)

      if (isValidPhone(updatedPhoneNumber)) {
        row[phoneColumn].value = updatedPhoneNumber
      } else {
        row[phoneColumn].info.push({
          message: 'Invalid Phone Number',
          level: 'error',
        })
      }
    },
    [updatePhoneValueSearch, isValidPhone],
  )

  const validateImportPhoneNumbers = useCallback(
    (record) => {
      const newRecord = { ...record }

      validatePhoneNumber('phoneNumber', 'phoneExt', newRecord.row)
      validatePhoneNumber('altPhoneNumber', 'altPhoneExt', newRecord.row)

      return newRecord
    },
    [validatePhoneNumber],
  )

  const validateCreationDate = useCallback(
    (record, mode: ActionMode) => {
      const newRecord = { ...record }

      if (mode === 'init') return newRecord

      const { createdDateTime } = newRecord.row

      createdDateTime.value = createdDateTime.value
        ? detectAndConvertDateTime(createdDateTime.value)
        : format(new Date(), localizationDateFormat)

      return newRecord
    },
    [detectAndConvertDateTime],
  )

  const validateCustomFieldDates = useCallback(
    (record) => {
      const newRecord = { ...record }

      const customFieldsWithDatesNames: string[] =
        customFieldData.customFields.reduce<string[]>((acc, curr) => {
          if (curr.type === 'Date') {
            acc.push(curr.name)
          }
          return acc
        }, [])

      customFieldsWithDatesNames.forEach((columnKey) => {
        if (newRecord.row[columnKey]) {
          newRecord.row[columnKey].value = newRecord.row[columnKey].value
            ? detectAndConvertDateTime(newRecord.row[columnKey].value)
            : undefined
        }
      })

      return newRecord
    },
    [customFieldData.customFields],
  )

  const validateDiscount = useCallback(
    (discountField: any, price: any) => {
      if (discountField && price) {
        discountField.info = []

        const fieldValue = discountField.value?.trim()

        if (price.value === '' && discountField.value) {
          discountField.info = [
            {
              message: 'Discount cannot be applied when no Price is provided',
              level: 'error',
            },
          ]
          return
        }

        if (fieldValue) {
          const sanitizedValue = Number(fieldValue.replace(/[%,]/g, '').trim())

          if (isNaN(sanitizedValue)) {
            discountField.info = [
              {
                message: 'Invalid Discount Value',
                level: 'error',
              },
            ]

            return
          }

          if (fieldValue.includes('%')) {
            if (sanitizedValue < 0 || sanitizedValue > 100) {
              discountField.info = [
                {
                  message: 'Discount % should be between 0 and 100',
                  level: 'error',
                },
              ]

              return
            }
          } else {
            if (sanitizedValue < 0) {
              discountField.info = [
                {
                  message: 'Cannot be a negative value',
                  level: 'error',
                },
              ]
              return
            }
            if (sanitizedValue > price.value) {
              discountField.info = [
                {
                  message: 'Discount cannot be greater than the Price',
                  level: 'error',
                },
              ]

              return
            }
          }

          discountField.value = fieldValue.includes('%')
            ? formatPercentage(fieldValue)
            : formatCurrency(fieldValue)
        }
      }

      return
    },
    [formatPercentage, formatCurrency],
  )

  const validateReferralFields = useCallback(
    (record) => {
      if (importSettings?.entityType === ImportEntityType.DraftLead) {
        return record
      }

      // if lead or opportunity then validate referral fields
      const newRecord = { ...record }

      const { referralType, referralValue } = newRecord.row

      if (!referralType && !referralValue) return newRecord

      referralValue.info = []
      if (referralType?.value == '' && referralValue?.value !== '') {
        referralValue.info = [
          {
            message: 'Referral Value will be ignored if Referral Type is empty',
            level: 'info',
          },
        ]
      }

      return newRecord
    },
    [importSettings],
  )

  const validateServiceFields = useCallback(
    (record) => {
      const newRecord = { ...record }
      const {
        serviceName,
        recurringDiscount,
        initialDiscount,
        frequencyId,
        recurringPrice,
        initialPrice,
        quantity,
      } = newRecord.row

      if (!serviceName) return newRecord

      const servFrequency = allServiceFrequencies.find(
        (x) => x.id === frequencyId?.value,
      )

      if (
        serviceName.value &&
        servFrequency &&
        servFrequency?.annualOccurrences > 1 &&
        (recurringPrice?.value === null || recurringPrice.value === '')
      ) {
        recurringPrice.info = [
          {
            message:
              'Required when Service is provided and Frequency is recurring',
            level: 'error',
          },
        ]
      } else {
        recurringPrice.info = []
      }

      if (importSettings?.entityType === ImportEntityType.Lead) {
        serviceName.info = []

        keysServiceFields.forEach(({ key, label, required }) => {
          if (Object.prototype.hasOwnProperty.call(newRecord.row, key)) {
            const serviceField = newRecord.row[key]

            if (
              required &&
              serviceName.value &&
              (serviceField?.value === null || serviceField.value === '')
            ) {
              serviceField.info = [
                {
                  message: 'Required when a Service is provided',
                  level: 'error',
                },
              ]
            } else if (!serviceName.value && serviceField?.value) {
              serviceName.info.push({
                message: `Service is required when ${label} is provided`,
                level: 'error',
              })
            } else {
              serviceField.info = []
            }
          }
        })
      } else {
        if (quantity) {
          quantity.info = []
        }
      }

      if (quantity?.value && !isNaN(quantity.value)) {
        if (Number(quantity.value) < 1) {
          quantity.info = [
            {
              message: 'Quantity must be greater than 0',
              level: 'error',
            },
          ]
        }
      }

      if (initialDiscount) validateDiscount(initialDiscount, initialPrice)
      if (recurringDiscount) validateDiscount(recurringDiscount, recurringPrice)

      return newRecord
    },
    [
      validateDiscount,
      keysServiceFields,
      allServiceFrequencies,
      allServices,
      importSettings,
    ],
  )

  const validateOwner = useCallback(
    (record) => {
      const newRecord = { ...record }

      const { assigneeId } = newRecord.row

      if (assigneeId?.value) {
        const assignee = allUsers.find(
          (user) =>
            `${user.fullName} - ${user.emailAddress}` ===
            assigneeId.value?.trim(),
        )

        if (assignee) {
          if (!assignee.isActive) {
            assigneeId.info = [
              {
                message: `Owner cannot be an inactive user`,
                level: 'error',
              },
            ]
          } else {
            const userAvailableInSalesTeam = assigneeId.selectOptions.find(
              (option) => option.value === assigneeId.resultValue,
            )

            // if user is not available in sales team
            if (!userAvailableInSalesTeam) {
              assigneeId.info = [
                {
                  message: `Owner does not exist`,
                  level: 'error',
                },
              ]
            } else assigneeId.info = []
          }
        } else if (!assignee && assigneeId.value !== 'Unassigned') {
          const userByFullName = allUsers.find(
            (user) => user.fullName === assigneeId.value?.trim(),
          )

          if (userByFullName && !userByFullName.isActive) {
            assigneeId.info = [
              {
                message: `Owner cannot be an inactive user`,
                level: 'error',
              },
            ]
          } else {
            assigneeId.info = [
              {
                message: `Owner does not exist`,
                level: 'error',
              },
            ]
          }
        } else assigneeId.info = []
      }
      return newRecord
    },
    [allUsers],
  )

  return {
    validateUSCAPostalCode,
    validateCreationDate,
    validateCustomFieldDates,
    validateImportPhoneNumbers,
    validateServiceFields,
    validateReferralFields,
    validateOwner,
  }
}
